/**
 * Malay formative evaluation / progress client (Phase 2D.5).
 * Uses gateway `/v1/tutor/formative-evaluation/*`. Tenant/learner identity
 * are never sent from the client for student reads.
 */
import { createHttpClient } from '../../../api/http';
import { resolveStudentTutorHttpBase } from '../../../api/tutorHttpBases';
import { serviceUrls } from '../../../config/serviceUrls';

function tutorRestApiPrefix(): string {
  const explicit = import.meta.env.VITE_TUTOR_REST_API_PREFIX?.trim();
  if (explicit === '/tutor' || explicit === '/v1/tutor') return explicit;
  return '/v1/tutor';
}

const PREFIX = `${tutorRestApiPrefix()}/formative-evaluation`;
const client = createHttpClient(resolveStudentTutorHttpBase());
const gatewayClient = createHttpClient(serviceUrls.gateway);
const GATEWAY_PREFIX = '/v1/tutor/formative-evaluation';

export type MasteryLevel =
  | 'not_enough_evidence'
  | 'beginning'
  | 'developing'
  | 'secure'
  | 'strong';

export type NarrativePlotPlanningStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | string;

export type NarrativeStoryDraftStatus =
  | 'not_started'
  | 'started'
  | 'in_progress'
  | string;

/** Phase 2E dual status on the Narrative Writing home card — no formal score. */
export interface NarrativeWritingHomeSlice {
  plot_planning_status?: NarrativePlotPlanningStatus;
  story_draft_status?: NarrativeStoryDraftStatus;
  last_saved_at?: string | null;
  latest_draft_id?: string | null;
  formal_score?: null;
  quality_percent?: null;
}

export interface ReadingPracticeHomeSlice {
  attempted_passages?: number;
  total_passages?: number;
  mastered_passages?: number;
  average_best_score?: number | null;
  last_practised_at?: string | null;
  mastery_threshold?: number;
}

/** Live Reading Tutor participation (malay_reading_lesson_state / turn audit). */
export interface ReadingTutorActivitySlice {
  session_count?: number;
  learner_turn_count?: number;
  tutor_turn_count?: number;
  last_activity_at?: string | null;
  has_activity?: boolean;
}

/** Unit 5 AI Tutor practice signals (topic visits, evaluate-turn sessions — not mastery). */
export interface Unit5TutorActivitySlice {
  has_practice_activity?: boolean;
  last_topic_id?: string | null;
  last_topic_visit_at?: string | null;
  formative_status?: string;
  formative_last_activity_at?: string | null;
  evaluate_session_count?: number;
  observation_count?: number;
}

export type AssessmentStatus = 'locked' | 'ready' | 'in_progress' | 'completed' | string;

export type TeachingStatus = 'not_started' | 'in_progress' | 'teaching_complete' | string;

export interface ExpositoryWritingPracticeSlice {
  status?: 'locked' | 'ready' | 'draft' | 'submitted' | 'reviewed' | string;
  writing_score?: number | null;
  latest_submission_version?: number;
  draft_id?: string | null;
  last_submitted_at?: string | null;
  last_autosaved_at?: string | null;
}

export interface Unit5LessonProgress {
  topic_id: string;
  title_ms: string;
  title_en: string;
  section_id?: string | null;
  status: 'not_started' | 'in_progress' | 'completed' | string;
  teaching_status?: TeachingStatus;
  teaching_progress_percent?: number;
  teaching_steps_completed?: string[];
  current_teaching_step?: string | null;
  assessment_status?: AssessmentStatus;
  teaching_completed_at?: string | null;
  ai_mastery_level?: MasteryLevel | null;
  mastery_display_ms?: string | null;
  mastery_display_en?: string | null;
  assessment_score?: number | null;
  assessment_progress_percent?: number;
  assessment_targets_total?: number;
  assessment_targets_completed?: number;
  assessment_targets_satisfied_count?: number;
  assessment_questions_answered?: number;
  assessment_mastery?: MasteryLevel | null;
  assessment_mastery_display_ms?: string | null;
  assessment_mastery_display_en?: string | null;
  assessment_feedback_summary?: string | null;
  assessment_strengths?: string[] | null;
  assessment_areas_for_improvement?: string[] | null;
  assessment_in_progress?: boolean;
  writing_practice?: ExpositoryWritingPracticeSlice | null;
  nested_labels?: string[];
  coverage_tags?: string[];
}

export interface Unit5ProgressSummary {
  ok: boolean;
  activity_key: string;
  completion: {
    completed_lessons: number;
    total_lessons: number;
    completion_ratio: number;
    completion_percent: number;
    unit_teaching_progress_percent?: number;
    assessments_completed?: number;
    assessments_in_progress?: number;
    assessment_completion_percent?: number;
    total_assessable_lessons?: number;
    status: string;
  };
  mastery_summary: {
    ai_mastery_level?: MasteryLevel | null;
    display_ms?: string | null;
    display_en?: string | null;
    has_evidence?: boolean;
  };
  continue_topic_id?: string | null;
  continue_lesson?: {
    topic_id?: string;
    title_ms?: string;
    teaching_progress_percent?: number;
    assessment_status?: AssessmentStatus;
    assessment_progress_percent?: number;
    assessment_score?: number | null;
  } | null;
  lessons: Unit5LessonProgress[];
  last_activity_at?: string | null;
}

export interface FormativeHomeCard {
  activity_key: string;
  progress?: {
    completed_units?: number;
    total_units?: number;
    completion_ratio?: number;
    status?: string;
    last_activity_at?: string;
    display?: { en?: string; ms?: string };
  } | null;
  unit5_progress?: Unit5ProgressSummary | null;
  strongest_skill?: {
    skill_key?: string;
    current_level?: MasteryLevel;
  } | null;
  recommended_next_step?: {
    skill_key?: string;
    message?: string;
  } | null;
  latest_feedback?: Record<string, unknown> | null;
  has_evidence?: boolean;
  narrative_writing?: NarrativeWritingHomeSlice | null;
  reading_practice?: ReadingPracticeHomeSlice | null;
}

export interface FormativeActivityDetail {
  ok: boolean;
  activity_key: string;
  progress?: Record<string, unknown> | null;
  skills: Array<{
    skill_key: string;
    current_level?: MasteryLevel;
    evidence_status?: string;
    trend?: string;
    valid_observation_count?: number;
    latest_reason?: string;
    /** False when narrative evidence is confirmation-only / progress proxy (not quality mastery). */
    counts_as_skill_evidence?: boolean;
    evidence_quality?: string;
    assessment_basis?: string;
    latest_evidence_type?: string;
  }>;
  feedback_history: Array<Record<string, unknown>>;
  observations: Array<Record<string, unknown>>;
  teacher_observations?: Array<Record<string, unknown>>;
  narrative_writing?: NarrativeWritingHomeSlice | null;
  reading_practice?: ReadingPracticeHomeSlice | null;
  reading_tutor_activity?: ReadingTutorActivitySlice | null;
  unit5_tutor_activity?: Unit5TutorActivitySlice | null;
}

export async function getMalayFormativeHome(): Promise<FormativeHomeCard[]> {
  const { data } = await client.get<{ cards: FormativeHomeCard[] }>(`${PREFIX}/me/home`);
  return Array.isArray(data?.cards) ? data.cards : [];
}

export async function getMalayFormativeActivityDetail(
  activityKey: string,
): Promise<FormativeActivityDetail> {
  const { data } = await client.get<FormativeActivityDetail>(
    `${PREFIX}/me/activities/${encodeURIComponent(activityKey)}`,
  );
  return data;
}

export async function getMalayUnit5Progress(): Promise<Unit5ProgressSummary> {
  const { data } = await client.get<Unit5ProgressSummary>(`${PREFIX}/me/unit5/progress`);
  return data;
}

export async function postUnit5RecordTeachingMilestone(body: {
  topic_id: string;
  step_id: string;
  session_id?: string;
  source?: string;
}): Promise<{
  ok: boolean;
  accepted?: boolean;
  topic_id?: string;
  step_ids?: string[];
  old_percent?: number;
  new_percent?: number;
  unit5_progress?: Unit5ProgressSummary;
}> {
  const { data } = await client.post(`${PREFIX}/me/unit5/record-teaching-milestone`, body);
  return data;
}

export async function postUnit5RecordTutorTurn(body: {
  topic_id: string;
  tutor_text: string;
  session_id?: string;
}): Promise<{
  ok: boolean;
  accepted?: boolean;
  inferred_step_ids?: string[];
  old_percent?: number;
  new_percent?: number;
  unit5_progress?: Unit5ProgressSummary;
}> {
  const { data } = await client.post(`${PREFIX}/me/unit5/record-tutor-turn`, body);
  return data;
}

export async function postUnit5BeginAssessment(body: {
  topic_id: string;
}): Promise<{
  ok: boolean;
  topic_id: string;
  mode?: string;
  session_mode?: string;
  begin_exit_check?: boolean;
  exit_check_started?: boolean;
  assessment_instruction_block?: string;
  assessment_system_instruction?: string;
  unit5_progress?: Unit5ProgressSummary;
}> {
  const { data } = await client.post(`${PREFIX}/me/unit5/begin-assessment`, body);
  return data;
}

export async function getUnit5ResumeAssessment(topicId: string): Promise<{
  ok: boolean;
  topic_id: string;
  mode?: string;
  session_mode?: string;
  begin_exit_check?: boolean;
  exit_check_started?: boolean;
  assessment_system_instruction?: string;
  unit5_progress?: Unit5ProgressSummary;
}> {
  const { data } = await client.get(`${PREFIX}/me/unit5/resume-assessment`, {
    params: { topic_id: topicId },
  });
  return data;
}

export async function postUnit5EvaluateTurn(body: {
  session_id: string;
  evidence_turn_id: string;
  learner_text: string;
  topic_id: string;
  intent_hint?: string;
  assessment_phase?: 'formative' | 'exit_check' | string;
  is_unclear?: boolean;
  is_retry?: boolean;
}): Promise<unknown> {
  const { data } = await client.post(`${PREFIX}/me/unit5/evaluate-turn`, body);
  return data;
}

export async function getTeacherLearnerFormativeActivity(params: {
  learnerId: string;
  activityKey: string;
}): Promise<FormativeActivityDetail> {
  // Authorisation is roster-backed on the server; do not send client student_ids as a trust gate.
  const { data } = await gatewayClient.get<FormativeActivityDetail>(
    `${GATEWAY_PREFIX}/teacher/learners/${encodeURIComponent(params.learnerId)}/activities/${encodeURIComponent(params.activityKey)}`,
  );
  return data;
}

export async function postTeacherFormativeOverride(body: {
  learner_id: string;
  activity_key: string;
  skill_key: string;
  overridden_level: Exclude<MasteryLevel, 'not_enough_evidence'>;
  reason: string;
}): Promise<unknown> {
  const { data } = await gatewayClient.post(`${GATEWAY_PREFIX}/teacher/overrides`, body);
  return data;
}
